"use client"

import { createContext } from "react"
import {
  TYPESENSE_APIKEY_IN_STORAGE,
  TYPESENSE_PARTY_APIKEY_IN_STORAGE,
  useTypesenseApiKey,
} from "./useUiTypesenseClient"

export interface TypesenseProviderProps {
  typesenseHost: string
  typesenseApiKeyMutation: string | object
  typesenseMultiCollectionApiKeyMutation?: string | object
  typesensePartyApiKeyMutation?: string | object
  children: React.ReactNode
}

export interface TypesenseContextProps {
  typesenseHost: string
  typesenseApiKeyMutation: string
  typesenseMultiCollectionApiKeyMutation: string
  apiKey?: string
  refreshKeys?: () => void
  isRefreshing?: boolean
  multiCollectionKeysV2?: {
    exports?: {
      exportCertificates?: string
      transferRequests?: string
      sourceTransfers?: string
      registration?: string
      registrationAll?: string
      supportingDocuments?: string
    }
    transfers?: {
      transferRequests?: string
      sourceTransfers?: string
      registration?: string
      registrationAll?: string
      supportingDocuments?: string
    }
    supportingDocs?: {
      registration?: string
      registrationAll?: string
      supportingDocuments?: string
    }
    products?: string
    exporters?: string
    agency?: string
    expires?: string
    partyApiKey?: string
  }
}

export const TypesenseContext = createContext<TypesenseContextProps>({
  typesenseHost: "",
  typesenseApiKeyMutation: "",
  typesenseMultiCollectionApiKeyMutation: "",
})

export const TypesenseProvider = (props: TypesenseProviderProps) => {
  const mutationToUse =
    props.typesenseMultiCollectionApiKeyMutation ||
    props.typesensePartyApiKeyMutation

  if (!props.typesenseApiKeyMutation || !mutationToUse) {
    console.warn(
      "for typesense, recommend to config its typesenseApiKeyMutation and typesenseMultiCollectionApiKeyMutation",
    )
  }

  const {
    apiKey,
    refreshKeys: refreshSingleKey,
    isRefreshing: isRefreshingSingle,
  } = useTypesenseApiKey({
    mutation:
      typeof props.typesenseApiKeyMutation === "string"
        ? props.typesenseApiKeyMutation
        : props.typesenseApiKeyMutation.toString(),
    localStorageKey: TYPESENSE_APIKEY_IN_STORAGE,
  })

  const {
    apiKey: multiCollectionKeysRaw,
    refreshKeys: refreshMultiKeys,
    isRefreshing: isRefreshingMulti,
  } = useTypesenseApiKey({
    mutation: mutationToUse
      ? typeof mutationToUse === "string"
        ? mutationToUse
        : mutationToUse.toString()
      : "",
    localStorageKey: TYPESENSE_PARTY_APIKEY_IN_STORAGE,
  })

  const refreshAllKeys = () => {
    refreshSingleKey()
    refreshMultiKeys()
  }

  // Parse multi-collection keys if available and it's a JSON string
  let multiCollectionKeys: TypesenseContextProps["multiCollectionKeysV2"] =
    undefined
  if (multiCollectionKeysRaw && typeof multiCollectionKeysRaw === "string") {
    try {
      multiCollectionKeys = JSON.parse(multiCollectionKeysRaw)
    } catch {
      // If parsing fails, it's a single key, not multi-collection
      multiCollectionKeys = undefined
    }
  } else if (
    multiCollectionKeysRaw &&
    typeof multiCollectionKeysRaw === "object"
  ) {
    multiCollectionKeys = multiCollectionKeysRaw
  }

  return (
    <TypesenseContext.Provider
      value={{
        typesenseHost: props.typesenseHost,
        typesenseApiKeyMutation:
          typeof props.typesenseApiKeyMutation === "string"
            ? props.typesenseApiKeyMutation
            : JSON.stringify(props.typesenseApiKeyMutation),
        typesenseMultiCollectionApiKeyMutation:
          typeof props.typesenseMultiCollectionApiKeyMutation === "string"
            ? props.typesenseMultiCollectionApiKeyMutation
            : JSON.stringify(
                props.typesenseMultiCollectionApiKeyMutation || "",
              ),
        apiKey: apiKey,
        refreshKeys: refreshAllKeys,
        isRefreshing: isRefreshingSingle || isRefreshingMulti,
        multiCollectionKeysV2: multiCollectionKeys,
      }}
    >
      {props.children}
    </TypesenseContext.Provider>
  )
}
